// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A form-integrated shipping carrier tracking label component.
 * This module provides a dynamic link to carrier tracking pages based on
 * form data, integrating with react-hook-form for state management.
 * The component automatically handles different carrier URL formats and
 * validates tracking information before displaying links.
 *
 * Key features:
 * - Dynamic carrier URL generation
 * - Form state integration
 * - Validation handling
 * - Null safety
 * - Collapsible display
 * - External link support
 * - Type safety
 */
import { ExternalLink } from '@/components/external-link';
import {
  ShippingCarrier,
  getTrackingURLForShippingCarrier,
  shippingCarrierSchema,
} from '@/constants/shipping-carrier';
import { Collapse } from '@mui/material';
import { Control, FieldPath, FieldValues, useWatch } from 'react-hook-form';
import { z } from 'zod';

/**
 * Schema for validating carrier tracking information.
 * Transforms empty tracking numbers to null for consistent handling.
 * This ensures that incomplete tracking information is handled uniformly
 * throughout the application.
 *
 * Structure:
 * - carrier: Validated shipping carrier enum value
 * - trackingNumber: Non-empty string or null
 *
 * @example
 * ```ts
 * const valid = {
 *   carrier: 'UPS',
 *   trackingNumber: '1Z999AA1234567890'
 * };
 *
 * const invalid = {
 *   carrier: null,
 *   trackingNumber: ''  // Transformed to null
 * };
 * ```
 */
const carrierTrackingValueSchema = z.object({
  carrier: shippingCarrierSchema.nullable(),
  trackingNumber: z.string().transform((value) => {
    if (value === '') {
      return null;
    }

    return value;
  }),
});

/**
 * Schema for validating the complete property structure.
 * Wraps the carrier tracking value in a property object to match
 * the form data structure used in the application.
 *
 * @example
 * ```ts
 * const property = {
 *   value: {
 *     carrier: 'FEDEX',
 *     trackingNumber: '123456789012'
 *   }
 * };
 * ```
 */
const propertySchema = z.object({ value: carrierTrackingValueSchema });

/**
 * Generates a tracking URL for a shipment based on carrier and tracking number.
 * Returns null if either parameter is missing to prevent invalid URLs.
 * Each carrier has its own URL format handled by the getTrackingURLForShippingCarrier
 * utility.
 *
 * @param carrier - The shipping carrier handling the shipment (e.g., 'UPS', 'FEDEX')
 * @param trackingIdentifier - The carrier's tracking number
 * @returns The tracking URL or null if information is incomplete
 *
 * @example
 * ```ts
 * // Returns "https://www.ups.com/track?tracknum=1Z999AA1234567890"
 * const url = getURL('UPS', '1Z999AA1234567890');
 *
 * // Returns null
 * const invalid = getURL(null, '1234');
 * ```
 */
function getURL(
  carrier: ShippingCarrier | null,
  trackingIdentifier: string | null
) {
  if (carrier === null || trackingIdentifier === null) {
    return null;
  }

  return getTrackingURLForShippingCarrier(carrier, trackingIdentifier);
}

/**
 * Props for configuring the FormShippingCarrierTrackingLabel component.
 * @typedef {Object} Props
 * @template Values - Type of the form values
 */
interface Props<Values extends FieldValues> {
  /**
   * Field path in the form for the tracking information.
   * Must point to an object matching the carrier tracking schema.
   *
   * Example paths:
   * - 'shipping.tracking'
   * - 'events.0.tracking'
   * - 'currentShipment.carrier'
   */
  name: FieldPath<Values>;

  /**
   * React Hook Form control object.
   * Used to watch field values and manage form state.
   * Must be from the same form instance as the field path.
   */
  control: Control<Values>;
}

/**
 * A form-integrated label that displays a tracking link for shipments.
 * Dynamically generates and displays a link to the carrier's tracking
 * page based on form data. The link is only shown when valid tracking
 * information is available.
 *
 * Features:
 * - Dynamic URL generation based on carrier
 * - Automatic validation of tracking data
 * - Collapsible display for incomplete information
 * - External link handling for carrier websites
 * - Type-safe form integration
 * - Null safety for missing data
 *
 * Behavior:
 * - Shows link only when both carrier and tracking number are present
 * - Collapses when tracking information is incomplete
 * - Opens carrier website in new tab
 * - Updates automatically with form changes
 *
 * Usage example:
 * ```tsx
 * const { control } = useForm({
 *   defaultValues: {
 *     shipping: {
 *       value: {
 *         carrier: 'UPS',
 *         trackingNumber: '1Z999AA1234567890'
 *       }
 *     }
 *   }
 * });
 *
 * return (
 *   <FormShippingCarrierTrackingLabel
 *     name="shipping"
 *     control={control}
 *   />
 * );
 * ```
 *
 * @template Values - Type of the form values
 * @param props - Component configuration and form integration options
 */
export function FormShippingCarrierTrackingLabel<Values extends FieldValues>(
  props: Props<Values>
) {
  const property = useWatch({ control: props.control, name: props.name });

  const result = propertySchema.safeParse(property);

  if (result.success) {
    const url = getURL(
      result.data.value.carrier,
      result.data.value.trackingNumber
    );

    const isOpen = url !== null;

    // Unfortunately the Collapse component from Material UI can't validate the
    // types of children and the ExternalLink component requires a valid link.
    const href = url ?? '#';

    return (
      <Collapse in={isOpen}>
        <ExternalLink href={href} variant="caption">
          View tracking on carrier website
        </ExternalLink>
      </Collapse>
    );
  }

  return null;
}
